// src/pages/PortalProfilePage.js
import React, { useState, useEffect } from 'react';
import portalApi from '../utils/portalApi';
import { usePortalAuth } from '../context/PortalAuthContext';
import toast from 'react-hot-toast';

export default function PortalProfilePage() {
  const { member, isAdmin } = usePortalAuth();
  const [profile, setProfile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  const load = () => {
    Promise.all([
      portalApi.get('/portal/profile'),
      portalApi.get('/portal/contacts'),
    ]).then(([p, c]) => {
      setProfile(p.data);
      setContacts(c.data);
      setForm({
        phone:        p.data.phone || '',
        website:      p.data.website || '',
        email:        p.data.email || '',
        addressLine1: p.data.address_line1 || '',
        addressLine2: p.data.address_line2 || '',
        city:         p.data.city || '',
        state:        p.data.state || '',
        zip:          p.data.zip || '',
      });
    }).catch(() => toast.error('Failed to load profile.'));
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await portalApi.patch('/portal/profile', form);
      toast.success('Profile updated.');
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  if (!profile) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Business</h1>
          <p className="page-subtitle">{profile.business_name}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setShowChangePw(true)}>Change Password</button>
          {isAdmin() && !editing && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>Edit</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Business Information</span></div>
          <div className="card-body">
            {!editing ? (
              <>
                <InfoRow label="Business Name"  value={profile.business_name} />
                <InfoRow label="Email"          value={profile.email} />
                <InfoRow label="Phone"          value={profile.phone} />
                <InfoRow label="Website"        value={profile.website} link />
                <InfoRow label="Address"        value={[profile.address_line1, profile.address_line2, profile.city && `${profile.city}, ${profile.state} ${profile.zip}`].filter(Boolean).join('\n')} />
                <InfoRow label="Industry"       value={profile.industry} />
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input className="form-input" value={form.website} onChange={e => set('website', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Address Line 1</label>
                  <input className="form-input" value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Address Line 2</label>
                  <input className="form-input" value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input className="form-input" value={form.state} onChange={e => set('state', e.target.value)} maxLength={2} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP</label>
                    <input className="form-input" value={form.zip} onChange={e => set('zip', e.target.value)} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Membership</span></div>
          <div className="card-body">
            <InfoRow label="Type"         value={profile.membership_type} />
            <InfoRow label="Annual Dues"  value={profile.annual_dues ? `$${parseFloat(profile.annual_dues).toLocaleString()}` : '—'} />
            <InfoRow label="Status"       value={<span className={`badge badge-${profile.status}`}>{profile.status?.replace('_',' ')}</span>} />
            <InfoRow label="Member Since" value={fmtDate(profile.member_since)} />
            <InfoRow label="Next Renewal" value={fmtDate(profile.next_renewal_date)} />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header">
          <span className="card-title">Contacts</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
            {isAdmin() ? `${contacts.length} on file` : 'Your record'}
          </span>
        </div>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Name</th><th>Title</th><th>Email</th><th>Phone</th><th>Type</th><th>Portal Role</th></tr></thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr><td colSpan={6}><div className="empty-state" style={{ padding: 24 }}><p>No contacts visible.</p></div></td></tr>
              ) : contacts.map(c => (
                <tr key={c.id}>
                  <td className="td-primary">{c.first_name} {c.last_name}</td>
                  <td>{c.title || '—'}</td>
                  <td>{c.email}</td>
                  <td>{c.phone || '—'}</td>
                  <td><span className="badge" style={{ fontSize: '0.65rem' }}>{c.contact_type?.replace(/_/g,' ')}</span></td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{c.portal_role?.replace('_',' ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const { logout } = usePortalAuth();

  const handleSave = async () => {
    if (next.length < 8) return toast.error('New password must be at least 8 characters.');
    if (next !== confirm) return toast.error('Passwords do not match.');
    setSaving(true);
    try {
      await portalApi.post('/auth/member/change-password', { currentPassword: current, newPassword: next });
      toast.success('Password changed. Please sign in again.');
      await logout();
      window.location.href = '/portal/login';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Change Password</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input type="password" className="form-input" value={current} onChange={e => setCurrent(e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input type="password" className="form-input" value={next} onChange={e => setNext(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input type="password" className="form-input" value={confirm} onChange={e => setConfirm(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, link }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--gray-500)', width: 130, flexShrink: 0, fontWeight: 500 }}>{label}</span>
      {link && typeof value === 'string' ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" style={{ color: 'var(--navy)' }}>{value}</a>
      ) : (
        <span style={{ color: 'var(--gray-900)', whiteSpace: 'pre-wrap' }}>{value}</span>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
